/**
 * Wishlist Feature Implementation for Shopify
 * 
 * This code implements a complete wishlist functionality for a Shopify store.
 * It includes:
 * 1. Adding/removing products to/from wishlist
 * 2. Storing wishlist items in local storage
 * 3. Creating a wishlist page
 * 4. Wishlist icon with counter in header
 * 5. Wishlist management functionalities
 */

// Wishlist functionality
class WishlistManager {
  constructor() {
    this.storageKey = 'thb-wishlist';
    this.wishlistItems = this.getWishlistItems();
    this.initWishlistButtons();
    this.updateWishlistCounters();
    this.setupEventListeners();
  }

  // Get wishlist items from local storage
  getWishlistItems() {
    const storedItems = localStorage.getItem(this.storageKey);
    return storedItems ? JSON.parse(storedItems) : [];
  }

  // Save wishlist items to local storage
  saveWishlistItems() {
    localStorage.setItem(this.storageKey, JSON.stringify(this.wishlistItems));
    this.updateWishlistCounters();
  }

  // Add item to wishlist
  addToWishlist(product) {
    if (!this.isInWishlist(product.id)) {
      this.wishlistItems.push(product);
      this.saveWishlistItems();
      return true;
    }
    return false;
  }

  // Remove item from wishlist
  removeFromWishlist(productId) {
    this.wishlistItems = this.wishlistItems.filter(item => item.id !== productId);
    this.saveWishlistItems();
  }

  // Check if product is in wishlist
  isInWishlist(productId) {
    return this.wishlistItems.some(item => item.id === productId);
  }

  // Toggle product in wishlist
  toggleWishlistItem(button) {
    const productCard = button.closest('product-card');
    const productId = button.getAttribute('data-product-id');
    const productHandle = button.getAttribute('data-product-handle');
    const productTitle = button.getAttribute('data-product-title');
    const productImage = button.getAttribute('data-product-image');
    const productUrl = button.getAttribute('data-product-url');
    const productPrice = button.getAttribute('data-product-price');
    const variantId = button.getAttribute('data-variant-id') || productId; // Add variant ID data attribute

    const product = {
      id: productId,
      handle: productHandle,
      title: productTitle,
      image: productImage,
      url: productUrl,
      price: productPrice,
      variantId: variantId // Store variant ID for cart operations
    };

    if (this.isInWishlist(productId)) {
      this.removeFromWishlist(productId);
      button.classList.remove('in-wishlist');
      button.setAttribute('aria-label', 'Add to Wishlist');
      this.showNotification('Product removed from wishlist');
    } else {
      this.addToWishlist(product);
      button.classList.add('in-wishlist');
      button.setAttribute('aria-label', 'Remove from Wishlist');
      this.showNotification('Product added to wishlist');
    }
  }

  // Initialize wishlist buttons on page load
  initWishlistButtons() {
    const wishlistButtons = document.querySelectorAll('.wishlist-button');
    
    wishlistButtons.forEach(button => {
      const productId = button.getAttribute('data-product-id');
      
      if (this.isInWishlist(productId)) {
        button.classList.add('in-wishlist');
        button.setAttribute('aria-label', 'Remove from Wishlist');
      } else {
        button.classList.remove('in-wishlist');
        button.setAttribute('aria-label', 'Add to Wishlist');
      }
    });
  }

  // Update all wishlist counters in the UI
  updateWishlistCounters() {
    const count = this.wishlistItems.length;
    const counters = document.querySelectorAll('.wishlist-counter');
    
    counters.forEach(counter => {
      counter.textContent = count;
      counter.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  // Show notification when adding/removing from wishlist
  showNotification(message) {
    const notification = document.getElementById('wishlist-notification') || this.createNotificationElement();
    notification.querySelector('.notification-message').textContent = message;
    notification.classList.add('show');
    
    clearTimeout(this.notificationTimeout);
    this.notificationTimeout = setTimeout(() => {
      notification.classList.remove('show');
    }, 3000);
  }

  // Create notification element if it doesn't exist
  createNotificationElement() {
    const notification = document.createElement('div');
    notification.id = 'wishlist-notification';
    notification.className = 'wishlist-notification';
    notification.innerHTML = '<div class="notification-message"></div>';
    document.body.appendChild(notification);
    return notification;
  }

  // Setup event listeners for wishlist actions
  setupEventListeners() {
    // Listen for wishlist button clicks
    document.addEventListener('click', event => {
      if (event.target.closest('.wishlist-button')) {
        const button = event.target.closest('.wishlist-button');
        this.toggleWishlistItem(button);
      }
    });

    // Listen for clear wishlist button clicks in wishlist page
    const clearWishlistBtn = document.getElementById('clear-wishlist');
    if (clearWishlistBtn) {
      clearWishlistBtn.addEventListener('click', () => {
        if (confirm('Are you sure you want to clear your wishlist?')) {
          this.wishlistItems = [];
          this.saveWishlistItems();
          this.renderWishlistPage();
          this.showNotification('Wishlist cleared');
        }
      });
    }
  }

  // Render wishlist page
  renderWishlistPage() {
    const wishlistContainer = document.getElementById('wishlist-container');
    if (!wishlistContainer) return;

    if (this.wishlistItems.length === 0) {
      wishlistContainer.innerHTML = `
        <div class="empty-wishlist">
          <div class="empty-wishlist-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </div>
          <h2>Your wishlist is empty</h2>
          <p>Browse our products and add your favorites to your wishlist</p>
          <a href="/collections/all" class="button accent">Browse Products</a>
        </div>
      `;
      return;
    }

    let wishlistHTML = `
      <div class="wishlist-header">
        <h1></h1>
        <div class="wishlist-actions" style="margin-top:20px">
          <button id="clear-wishlist" class="button accent outline small">Clear Wishlist</button>
        </div>
      </div>
      <div class="wishlist-grid">
    `;

    this.wishlistItems.forEach(item => {
      // Use variantId if available, fallback to id if not
      const cartId = item.variantId || item.id;
      
      wishlistHTML += `
        <div class="wishlist-item" data-product-id="${item.id}">
          <button class="remove-from-wishlist" data-product-id="${item.id}" aria-label="Remove from wishlist">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          <a href="${item.url}" class="wishlist-item-image">
            <img src="${item.image}" alt="${item.title}" loading="lazy">
          </a>
          <div class="wishlist-item-info">
            <h3 class="wishlist-item-title">
              <a href="${item.url}">${item.title}</a>
            </h3>
            <div class="wishlist-item-price">${item.price}</div>
            <button class="button accent small add-to-cart" data-product-id="${item.id}" data-variant-id="${cartId}">Add to Cart</button>
          </div>
        </div>
      `;
    });

    wishlistHTML += '</div>';
    wishlistContainer.innerHTML = wishlistHTML;

    // Set up event listeners for the wishlist page
    this.setupWishlistPageEvents();
  }

  // Setup event listeners specific to the wishlist page
  setupWishlistPageEvents() {
    // Remove item buttons
    const removeButtons = document.querySelectorAll('.remove-from-wishlist');
    removeButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        const productId = button.getAttribute('data-product-id');
        this.removeFromWishlist(productId);
        
        // Remove item from UI
        const wishlistItem = button.closest('.wishlist-item');
        wishlistItem.style.animation = 'fadeOut 0.3s';
        setTimeout(() => {
          wishlistItem.remove();
          if (this.wishlistItems.length === 0) {
            this.renderWishlistPage();
          }
        }, 300);

        this.showNotification('Product removed from wishlist');
      });
    });

    // Add to cart buttons
    const addToCartButtons = document.querySelectorAll('.wishlist-item .add-to-cart');
    addToCartButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        // Get variant ID for cart operations (this is the key change)
        const variantId = button.getAttribute('data-variant-id');
        
        if (!variantId) {
          console.error('No variant ID found for this product');
          alert('Error: Could not add item to cart. Missing variant information.');
          return;
        }
        
        button.classList.add('loading');
        button.innerHTML = `
          <svg class="loading-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M12 6v6l4 2"></path>
          </svg>
          Adding...
        `;
        
        // Log the request for debugging
        console.log('Adding to cart:', {
          id: variantId,
          quantity: 1
        });
        
        // Add to cart using Shopify AJAX API
        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then(data => {
          console.log('Added to cart successfully:', data);
          button.classList.remove('loading');
          button.classList.add('added');
          button.innerHTML = `Added to Cart`;
          
          // Update cart count and refresh mini-cart if it exists
          if (typeof refreshCart === 'function') {
            refreshCart();
          }

          // Show notification
          this.showNotification('Product added to cart');

          // Find and activate the Cart-Drawer
          const cartDrawer = document.getElementById('Cart-Drawer');
          if (cartDrawer) {
            cartDrawer.classList.add('active');
            document.body.classList.add('open-cc');
            // Refresh the Cart-Drawer content
            const event = new CustomEvent('cart:refresh', {
              bubbles: true,
              detail: { cart: data }
            });
            document.dispatchEvent(event);
          }
          
          setTimeout(() => {
            button.classList.remove('added');
            button.innerHTML = `Add to Cart`;
          }, 2000);
        })
        .catch(error => {
          console.error('Error adding to cart:', error);
          button.classList.remove('loading');
          button.innerHTML = `Add to Cart`;
          alert('Error adding item to cart. Please try again.');
        });
      });
    });

    // Setup clear wishlist button again (as we recreated the DOM)
    const clearWishlistBtn = document.getElementById('clear-wishlist');
    if (clearWishlistBtn) {
      clearWishlistBtn.addEventListener('click', () => {
        if (confirm('Are you sure you want to clear your wishlist?')) {
          this.wishlistItems = [];
          this.saveWishlistItems();
          this.renderWishlistPage();
          this.showNotification('Wishlist cleared');
        }
      });
    }
  }
}

// Initialize wishlist on document ready
document.addEventListener('DOMContentLoaded', function() {
  window.wishlistManager = new WishlistManager();
  
  // If we're on the wishlist page, render it
  if (document.getElementById('wishlist-container')) {
    window.wishlistManager.renderWishlistPage();
  }
});